import type { Color } from '@pz/scene';

/**
 * Picking ink that shows up on the product.
 *
 * A shopper who types "Mila" on a navy teddy shirt must not get black letters
 * they can barely see — nor have to know that the colour tool exists. The
 * canvas samples the product picture behind the zone, and new words take the
 * palette colour with the best WCAG contrast against it. Nothing changes when
 * the shop's first colour already reads well (a white shirt keeps black ink).
 */

type Rgb = [number, number, number];

export function hexToRgb(hex: string): Rgb | undefined {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return undefined;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG 2 relative luminance, 0 (black) to 1 (white). */
export function luminance([r, g, b]: Rgb): number {
  const channel = (value: number): number => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The palette colour to write with on `background`, or `undefined` when the
 * first (default) colour already has a contrast of at least `enough`.
 */
export function readableInk(background: Rgb, palette: readonly Color[], enough = 3): Color | undefined {
  const first = palette[0];
  const firstRgb = first && hexToRgb(first.hex);
  if (firstRgb && contrastRatio(firstRgb, background) >= enough) return undefined;
  let best: Color | undefined, bestRatio = 0;
  for (const colour of palette) {
    const rgb = hexToRgb(colour.hex);
    if (!rgb) continue;
    const ratio = contrastRatio(rgb, background);
    if (ratio > bestRatio) { best = colour; bestRatio = ratio; }
  }
  return best && best !== first ? best : undefined;
}
